// Opens the tutor name poll after a call. Startup code (the two call surfaces) imports this, so it stays
// tiny: no modal, no catalog, no network. The modal itself is lazy-loaded by DeferredGlobalModals.
import { create } from "zustand";
import { TUTOR_NAME_POLL_ID } from "./tutorNamePollOptions";
import { getVoiceCallStartCount } from "../../voice/core/voiceCallStarts";
import type { UserData } from "../../constants/GlobalTypesAndEnumsAndConstants";

// The call that just ended, for the poll's analytics (content-free).
export interface TutorNamePollCallContext {
  surface: string;
  connectedDurationSeconds: number;
  // Voice calls started in this page load when the poll was offered: if another call starts before the
  // poll can open (it waits on the Worker), the poll stays closed rather than covering the live call.
  voiceCallStartCountAtOffer: number;
}

/** Whether a voice call has started since this poll was offered (the poll must not cover it). */
export function hasVoiceCallStartedSinceOffer(callContext: TutorNamePollCallContext): boolean {
  return getVoiceCallStartCount() !== callContext.voiceCallStartCountAtOffer;
}

interface TutorNamePollStoreState {
  callContext: TutorNamePollCallContext | null;
  openAfterCall: (callContext: TutorNamePollCallContext) => void;
  close: () => void;
}

export const useTutorNamePollStore = create<TutorNamePollStoreState>((set) => ({
  callContext: null,
  openAfterCall: (callContext) => set({ callContext }),
  close: () => set({ callContext: null }),
}));

// A call shorter than this is a misdial or a quick test; asking to name the tutor then would feel random.
export const TUTOR_NAME_POLL_MINIMUM_CALL_SECONDS = 30;

// The owner's rule (2026-11-04): a learner is asked once. After the modal has shown, whether they voted or
// closed it, it never shows again for that account. Three records enforce it: this device's (immediate,
// survives a reload), the account's `user_settings.tutor_name_poll_shown_at[pollId]` (other devices), and
// the Worker's vote (a learner who voted anywhere).
function deviceRecordKey(userId: string): string {
  return `tutor_name_poll:${TUTOR_NAME_POLL_ID}:${userId}`;
}

export function hasThisDeviceAskedTutorNamePoll(userId: string): boolean {
  try {
    return JSON.parse(localStorage.getItem(deviceRecordKey(userId)) ?? "null")?.hasBeenAsked === true;
  } catch {
    return false;
  }
}

export function rememberTutorNamePollAskedOnThisDevice(userId: string): void {
  try {
    localStorage.setItem(deviceRecordKey(userId), JSON.stringify({ hasBeenAsked: true }));
  } catch {
    // Storage full or blocked: the account record still keeps it from showing again.
  }
}

/** Whether this account was already shown the poll, on any device (the account record). */
export function hasAccountBeenAskedTutorNamePoll(user: Pick<UserData, "user_settings"> | null | undefined): boolean {
  const shownAt = user?.user_settings?.tutor_name_poll_shown_at;
  return Boolean(shownAt && typeof shownAt === "object" && (shownAt as Record<string, unknown>)[TUTOR_NAME_POLL_ID]);
}

/**
 * Called when a learner hangs up an ordinary tutor call (not a feedback call, a challenge or a clinical
 * station, and not a call the free limit or the idle timer ended). Opens the poll unless the call was too
 * short or this learner was already asked. The container then asks the Worker, which has the final word on
 * whether this account already voted.
 */
export function offerTutorNamePollAfterCall(args: {
  user: Pick<UserData, "id" | "user_settings"> | null | undefined;
  surface: string;
  connectedDurationSeconds: number;
}): void {
  const userId = args.user?.id;
  if (!userId) return;
  if (args.connectedDurationSeconds < TUTOR_NAME_POLL_MINIMUM_CALL_SECONDS) return;
  if (hasThisDeviceAskedTutorNamePoll(userId) || hasAccountBeenAskedTutorNamePoll(args.user)) return;
  useTutorNamePollStore.getState().openAfterCall({
    surface: args.surface,
    connectedDurationSeconds: args.connectedDurationSeconds,
    voiceCallStartCountAtOffer: getVoiceCallStartCount(),
  });
}
